import { addHours } from 'date-fns/addHours';
import { addMinutes } from 'date-fns/addMinutes';
import { format } from 'date-fns/format';
import { startOfHour } from 'date-fns/startOfHour';

import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import {
  appFill,
  appStroke,
  formatClockWithSeconds,
  formatClockRange,
  formatClockRangeWithSeconds,
  formatDuration,
  hiddenByWords,
} from './activity-timeline.format';
import {
  cardsEndedAt,
  UNKNOWN_APP,
  type ActivityDayShape,
  type DeviceColumn,
  type FocusCard,
  type ObservationSpan,
  type Span,
} from './activity-timeline.model';
import { ZOOM_LEVELS } from './activity-timeline.zoom';

import type { ActivitySnapshot } from './activity-timeline.query';

const MIN_ONE_LINE_HEIGHT_PX = 16;

const MIN_TWO_LINE_HEIGHT_PX = 32;

const MIN_LABEL_HEIGHT_PX = 16;

/**
 * Observation and presence have lanes of their own at the left of a column, because focus cards
 * cover the column whenever a window has focus and would hide bars drawn under them.
 */
const BAR_LANE_WIDTH_PX = 10;
const BAR_LANE_GAP_PX = 6;

/**
 * Gridline spacings, coarsest first. `gridlineIntervalMinutes` picks the finest one whose lines are
 * at least {@link MIN_GRIDLINE_SPACING_PX} apart.
 */
const GRIDLINE_INTERVAL_MINUTES = [60, 30, 15, 5, 2, 1];
const MIN_GRIDLINE_SPACING_PX = 40;

const gridlineIntervalMinutes = (pxPerMinute: number) =>
  GRIDLINE_INTERVAL_MINUTES.reduce((coarser, minutes) =>
    minutes * pxPerMinute >= MIN_GRIDLINE_SPACING_PX ? minutes : coarser
  );

/** Time-label gutter. `TRACK_INSET_CLASS` is the same width, so the two change together. */
export const GUTTER_CLASS = 'w-14';
const TRACK_INSET_CLASS = 'left-14';
export const COLUMN_GAP_CLASS = 'gap-3';

const HIDDEN_HATCH =
  'repeating-linear-gradient(45deg, transparent 0 4px, var(--border-strongest) 4px 6px)';
const LOCK_HATCH =
  'repeating-linear-gradient(-45deg, transparent 0 6px, var(--border-strong) 6px 8px)';

export type RailSelection = {
  intervalIds: ReadonlySet<string>;
  snapshotId: number | null;
};

type RailCallbacks = {
  onSelectInterval: (intervalId: string) => void;
  onSelectGroup: (cards: FocusCard[]) => void;
  onSelectSnapshot: (snapshot: ActivitySnapshot) => void;
};

export const ActivityColumnHeaders = ({
  shape,
}: {
  shape: ActivityDayShape;
}) => (
  <XStack className={cn('shrink-0 border-b border-border', COLUMN_GAP_CLASS)}>
    <div className={cn(GUTTER_CLASS, 'shrink-0')} />
    {shape.columns.map((column) => (
      <XStack
        key={column.deviceId}
        className="min-w-0 flex-1 items-baseline gap-3 py-1.5"
      >
        <span className="shrink-0 text-[10px] text-foreground-muted">
          observed · present
        </span>
        <span
          title={`${column.deviceId}\nobserved ${formatDuration(column.observedMs)} · ${column.cards.length} focus intervals`}
          // Truncated from the start, so a dev build's worktree suffix stays readable
          className="truncate text-left font-mono text-[11px] text-foreground-subtle [direction:rtl]"
        >
          {column.deviceId}
        </span>
      </XStack>
    ))}
  </XStack>
);

const Gridline = ({
  label,
  topPx,
  isHour,
}: {
  label: string;
  topPx: number;
  isHour: boolean;
}) => (
  <XStack
    className="pointer-events-none absolute inset-x-0 -translate-y-1/2 items-center"
    style={{ top: topPx }}
  >
    <span
      className={cn(
        GUTTER_CLASS,
        'shrink-0 pr-3 text-right text-[11px] tabular-nums',
        isHour ? 'text-foreground-subtle' : 'text-foreground-muted'
      )}
    >
      {label}
    </span>
    <div
      className={cn('h-px flex-1', isHour ? 'bg-border-strong' : 'bg-border')}
    />
  </XStack>
);

const ObservationBar = ({
  span,
  topPx,
  heightPx,
  isSelected,
  onSelect,
}: {
  span: ObservationSpan;
  topPx: number;
  heightPx: number;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    title={[
      `observation · ${formatClockRangeWithSeconds(span.startedAt, span.endedAt)}`,
      `opened on ${span.interval.openReason}`,
      span.isOpen
        ? 'still open'
        : `closed on ${span.interval.closeReason ?? 'unknown'}`,
      ...span.violations.map((violation) => violation.message),
    ].join('\n')}
    className={cn(
      'absolute inset-x-0 cursor-default rounded-sm border bg-border-strong',
      span.violations.length > 0 ? 'border-warning-8' : 'border-border-strong',
      isSelected && 'border-accent'
    )}
    style={{
      top: topPx,
      height: heightPx,
      borderBottomStyle: span.heartbeatTimedOut ? 'dashed' : undefined,
    }}
  />
);

const Band = ({
  topPx,
  heightPx,
  label,
  tooltip,
  hatched,
}: {
  topPx: number;
  heightPx: number;
  label: string;
  tooltip: string;
  hatched: boolean;
}) => (
  <div
    title={tooltip}
    className={cn(
      'absolute inset-x-0 flex items-start overflow-hidden px-2',
      hatched ? 'justify-end border-y border-border-strong' : 'justify-start'
    )}
    style={{
      top: topPx,
      height: heightPx,
      backgroundImage: hatched ? LOCK_HATCH : undefined,
    }}
  >
    {heightPx >= MIN_LABEL_HEIGHT_PX ? (
      <span className="rounded-sm bg-background/80 px-1 text-[11px] leading-4 text-foreground-muted">
        {label}
      </span>
    ) : null}
  </div>
);

const Tick = ({
  snapshot,
  topPx,
  isSelected,
  isOrphan,
  onSelect,
}: {
  snapshot: ActivitySnapshot;
  topPx: number;
  isSelected: boolean;
  isOrphan: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    data-snapshot-id={snapshot.id}
    title={[
      formatClockWithSeconds(snapshot.capturedAt),
      snapshot.title ?? snapshot.app ?? UNKNOWN_APP,
      snapshot.url,
      isOrphan ? 'inside no focus interval' : null,
    ]
      .filter(Boolean)
      .join('\n')}
    aria-pressed={isSelected}
    onClick={onSelect}
    className="group absolute right-0 flex h-3 w-5 -translate-y-1/2 items-center justify-end pr-1"
    style={{ top: topPx }}
  >
    <span
      className={cn(
        'h-px w-2.5 group-hover:h-0.5 group-hover:bg-foreground',
        !isOrphan && 'bg-foreground-muted',
        isOrphan && 'w-3 bg-foreground',
        isSelected && 'h-0.5 bg-accent group-hover:bg-accent'
      )}
    />
  </button>
);

const groupFill = (cards: readonly FocusCard[]) => {
  const byHue = new Map<number, number>();
  for (const card of cards) {
    byHue.set(card.hue, (byHue.get(card.hue) ?? 0) + card.durationMs);
  }
  const total = [...byHue.values()].reduce((sum, ms) => sum + ms, 0) || 1;
  let cursor = 0;
  const stops = [...byHue]
    .sort((one, other) => other[1] - one[1])
    .map(([hue, ms]) => {
      const from = cursor;
      cursor += (ms / total) * 100;
      return `${appFill(hue)} ${from.toFixed(1)}% ${cursor.toFixed(1)}%`;
    });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
};

const cardTooltip = (cards: readonly FocusCard[]) => {
  const first = cards[0];
  const lines =
    cards.length === 1
      ? [
          `${first.appName}${first.hiddenBy === null ? '' : ` · ${hiddenByWords(first.hiddenBy, first.appName)}`}`,
          first.label,
          `${formatClockRangeWithSeconds(first.startedAt, first.endedAt)} · ${formatDuration(first.durationMs)}${first.isOpen ? ' · still open' : ''}`,
          `present ${formatDuration(first.presentMs)} · ${first.snapshots.length} captures`,
          `opened on ${first.interval.openReason}${first.interval.closeReason === null ? '' : ` · closed on ${first.interval.closeReason}`}`,
          first.isDerived ? 'derived from captures, not recorded' : null,
        ]
      : [
          `${cards.length} focus intervals too short to draw at this zoom`,
          `${formatClockRangeWithSeconds(first.startedAt, cardsEndedAt(cards))}`,
          ...[...new Set(cards.map((card) => card.appName))].slice(0, 6),
          'zoom in to see them one by one',
        ];
  return [
    ...lines,
    ...cards
      .flatMap((card) => card.violations)
      .map((violation) => violation.message),
  ]
    .filter(Boolean)
    .join('\n');
};

const CardGroup = ({
  cards,
  topPx,
  railTopPxAt,
  heightPx,
  isMarker,
  lane,
  laneCount,
  pxPerMinute,
  dimmed,
  selection,
  callbacks,
}: {
  cards: FocusCard[];
  topPx: number;
  /**
   * The rail's time-to-pixel function, so a capture tick stays at its capture time when layout has
   * moved the card
   */
  railTopPxAt: (moment: Date) => number;
  heightPx: number;
  isMarker: boolean;
  lane: number;
  laneCount: number;
  pxPerMinute: number;
  dimmed: boolean;
  selection: RailSelection;
  callbacks: RailCallbacks;
}) => {
  const single = cards.length === 1 ? cards[0] : null;
  const startedAt = cards[0].startedAt;
  const endedAt = cardsEndedAt(cards);
  const snapshots = cards.flatMap((card) => card.snapshots);
  const violations = cards.flatMap((card) => card.violations);
  const isSelected =
    cards.some((card) => selection.intervalIds.has(card.interval.id)) ||
    snapshots.some((snapshot) => snapshot.id === selection.snapshotId);
  const offsetPx = (moment: Date) => railTopPxAt(moment) - topPx;
  const apps = [...new Set(cards.map((card) => card.appName))];

  if (isMarker) {
    return (
      <button
        type="button"
        title={cardTooltip(cards)}
        aria-pressed={isSelected}
        // A marker draws no ticks, so `revealSnapshot` scrolls to the marker when one of its
        // captures is selected
        data-snapshot-ids={snapshots.map((snapshot) => snapshot.id).join(' ')}
        onClick={() =>
          single === null
            ? callbacks.onSelectGroup(cards)
            : callbacks.onSelectInterval(single.interval.id)
        }
        className={cn(
          'absolute inset-x-0 z-[1] h-[3px] transition-opacity',
          isSelected && 'ring-2 ring-accent',
          dimmed && 'opacity-15'
        )}
        style={{
          top: topPx,
          background:
            violations.length > 0
              ? 'var(--warning-9)'
              : single === null
                ? 'var(--foreground-muted)'
                : appStroke(single.hue),
        }}
      >
        {single === null ? (
          <span className="absolute -top-2 right-1 rounded-sm border border-border bg-background px-1 text-[9px] leading-[13px] text-foreground-muted tabular-nums">
            {cards.length}
          </span>
        ) : null}
      </button>
    );
  }

  // The card and its ticks are siblings, so a tick is not a button inside a button
  return (
    <div
      className={cn('absolute transition-opacity', dimmed && 'opacity-15')}
      style={{
        top: topPx,
        height: heightPx,
        left: `calc(${(lane / laneCount) * 100}% + ${lane === 0 ? 0 : 2}px)`,
        width: `calc(${100 / laneCount}% - ${lane === 0 ? 0 : 2}px)`,
      }}
    >
      <button
        type="button"
        title={cardTooltip(cards)}
        aria-pressed={isSelected}
        onClick={() =>
          single === null
            ? callbacks.onSelectGroup(cards)
            : callbacks.onSelectInterval(single.interval.id)
        }
        className={cn(
          'absolute inset-0 overflow-hidden rounded-sm border border-l-[3px] text-left',
          violations.length > 0 ? 'border-warning-8' : 'border-border',
          cards[0].isDerived && 'border-dashed',
          isSelected && 'ring-2 ring-accent'
        )}
        style={{
          background: single === null ? groupFill(cards) : appFill(single.hue),
          borderLeftColor:
            single === null ? 'var(--border-strongest)' : appStroke(single.hue),
        }}
      >
        {single !== null && single.hiddenBy !== null ? (
          <div
            className="absolute inset-0"
            style={{ backgroundImage: HIDDEN_HATCH }}
          />
        ) : null}
        <div className="relative flex h-full flex-col justify-center gap-px pl-1.5 pr-5">
          {single === null ? (
            <span className="truncate text-xs leading-4">
              <span className="font-medium">{cards.length} intervals</span>
              <span className="text-foreground-subtle">
                {' '}
                · {apps.slice(0, 3).join(', ')}
                {apps.length > 3 ? ` +${apps.length - 3}` : ''}
              </span>
            </span>
          ) : (
            <span className="truncate text-xs leading-4">
              <span className="font-medium">{single.appName}</span>
              {single.hiddenBy !== null ? (
                <span className="text-foreground-subtle">
                  {' '}
                  · {hiddenByWords(single.hiddenBy, single.appName)}
                </span>
              ) : single.label === null ? null : (
                <span className="text-foreground-subtle"> {single.label}</span>
              )}
            </span>
          )}
          {heightPx >= MIN_TWO_LINE_HEIGHT_PX ? (
            <span className="truncate text-[11px] leading-4 text-foreground-muted tabular-nums">
              {formatDuration(endedAt.getTime() - startedAt.getTime())}
              {' · '}
              {snapshots.length} captures
              {single?.isOpen ? ' · still open' : ''}
              {single?.isDerived ? ' · derived from captures' : ''}
              {single === null &&
              pxPerMinute < ZOOM_LEVELS[ZOOM_LEVELS.length - 1]
                ? ' · zoom in to separate'
                : ''}
            </span>
          ) : null}
        </div>
      </button>
      {snapshots.map((snapshot) => (
        <Tick
          key={snapshot.id}
          snapshot={snapshot}
          topPx={offsetPx(new Date(snapshot.capturedAt))}
          isSelected={snapshot.id === selection.snapshotId}
          isOrphan={false}
          onSelect={() => callbacks.onSelectSnapshot(snapshot)}
        />
      ))}
    </div>
  );
};

type DrawnCardGroup = {
  key: string;
  cards: FocusCard[];
  topPx: number;
  heightPx: number;
  endedAt: Date;
  isMarker: boolean;
  lane: number;
  laneCount: number;
};

/**
 * Places cards that overlap in time side by side, the way a calendar places clashing events.
 */
const assignLanes = (items: DrawnCardGroup[]) => {
  let cluster: DrawnCardGroup[] = [];
  let clusterEndsAt = -Infinity;
  let laneEndsAt: number[] = [];
  const closeCluster = () => {
    for (const item of cluster) item.laneCount = laneEndsAt.length;
    cluster = [];
    laneEndsAt = [];
  };

  for (const item of items) {
    const startsAt = item.cards[0].startedAt.getTime();
    const endsAt = item.endedAt.getTime();
    if (startsAt >= clusterEndsAt) closeCluster();
    let lane = laneEndsAt.findIndex((laneEnd) => laneEnd <= startsAt);
    if (lane === -1) lane = laneEndsAt.length;
    laneEndsAt[lane] = endsAt;
    item.lane = lane;
    cluster.push(item);
    clusterEndsAt = Math.max(clusterEndsAt, endsAt);
  }
  closeCluster();
  return items;
};

/**
 * Groups each run of neighbouring cards that are too short to read at this zoom into one card.
 */
const layOutCards = (
  cards: readonly FocusCard[],
  topPxAt: (moment: Date) => number
): DrawnCardGroup[] => {
  const runs: FocusCard[][] = [];
  let run: FocusCard[] = [];
  let runEndedMs = -Infinity;
  const flush = () => {
    if (run.length > 0) runs.push(run);
    run = [];
    runEndedMs = -Infinity;
  };

  for (const card of cards) {
    const heightPx = topPxAt(card.endedAt) - topPxAt(card.startedAt);
    if (heightPx >= MIN_ONE_LINE_HEIGHT_PX) {
      flush();
      runs.push([card]);
      continue;
    }
    // A short card far from the run starts its own run, so that a group does not span a long empty
    // stretch
    if (
      run.length > 0 &&
      topPxAt(card.startedAt) - topPxAt(new Date(runEndedMs)) >
        MIN_ONE_LINE_HEIGHT_PX
    ) {
      flush();
    }
    run.push(card);
    runEndedMs = Math.max(runEndedMs, card.endedAt.getTime());
  }
  flush();

  const items: DrawnCardGroup[] = runs.map((members) => {
    const first = members[0];
    const endedAt = cardsEndedAt(members);
    const top = topPxAt(first.startedAt);
    return {
      key: first.key,
      cards: members,
      topPx: top,
      heightPx: topPxAt(endedAt) - top,
      endedAt,
      isMarker: false,
      lane: 0,
      laneCount: 1,
    };
  });

  // If its neighbours leave room, a short run grows to a readable height, moving up or down to fit.
  // Otherwise it is drawn as a marker, so that it does not cover a neighbour
  for (const [index, item] of items.entries()) {
    if (item.heightPx >= MIN_ONE_LINE_HEIGHT_PX) continue;
    const before = items[index - 1];
    const after = items[index + 1];
    // A neighbour that overlaps this run in time is drawn in another lane, so it does not limit the
    // room
    const startedAt = item.cards[0].startedAt.getTime();
    const roomTop =
      before === undefined || before.endedAt.getTime() > startedAt
        ? -Infinity
        : before.topPx + before.heightPx;
    const roomBottom =
      after === undefined ||
      item.endedAt.getTime() > after.cards[0].startedAt.getTime()
        ? Infinity
        : after.topPx;
    if (roomBottom - roomTop >= MIN_ONE_LINE_HEIGHT_PX) {
      item.topPx = Math.min(
        Math.max(item.topPx, roomTop),
        roomBottom - MIN_ONE_LINE_HEIGHT_PX
      );
      item.heightPx = MIN_ONE_LINE_HEIGHT_PX;
    } else {
      item.isMarker = true;
      item.heightPx = 3;
    }
  }

  return assignLanes(items);
};

const Column = ({
  column,
  topPxAt,
  railHeightPx,
  pxPerMinute,
  appFilter,
  selection,
  callbacks,
}: {
  column: DeviceColumn;
  topPxAt: (moment: Date) => number;
  railHeightPx: number;
  pxPerMinute: number;
  appFilter: string | null;
  selection: RailSelection;
  callbacks: RailCallbacks;
}) => {
  const drawn = layOutCards(column.cards, topPxAt);
  const heightOf = (span: Span) =>
    topPxAt(span.endedAt) - topPxAt(span.startedAt);
  const matchesFilter = (card: FocusCard) =>
    appFilter === null || card.appKey === appFilter;

  return (
    <div className="relative min-w-0 flex-1">
      {column.unobserved
        .filter((span) => topPxAt(span.endedAt) > 0)
        .map((span) => {
          const top = Math.max(topPxAt(span.startedAt), 0);
          return (
            <Band
              key={span.key}
              topPx={top}
              heightPx={Math.min(topPxAt(span.endedAt), railHeightPx) - top}
              label={`${span.reason} · ${formatDuration(span.durationMs)}`}
              tooltip={`unobserved · ${span.reason} · ${formatClockRange(span.startedAt, span.endedAt)}`}
              hatched={false}
            />
          );
        })}
      {column.locks.map((band) => (
        <Band
          key={band.key}
          topPx={topPxAt(band.startedAt)}
          heightPx={heightOf(band)}
          label={`locked · ${formatDuration(band.endedAt.getTime() - band.startedAt.getTime())}`}
          tooltip={`screen locked · ${formatClockRange(band.startedAt, band.endedAt)}`}
          hatched
        />
      ))}
      <div
        // Only the cards and ticks inside take the pointer, so that the bands under this box still
        // show their tooltips
        className="pointer-events-none absolute inset-y-0 right-0 [&>*]:pointer-events-auto"
        style={{ left: 2 * (BAR_LANE_WIDTH_PX + BAR_LANE_GAP_PX) }}
      >
        {drawn.map((item) => (
          <CardGroup
            key={item.key}
            cards={item.cards}
            topPx={item.topPx}
            railTopPxAt={topPxAt}
            heightPx={item.heightPx}
            isMarker={item.isMarker}
            lane={item.lane}
            laneCount={item.laneCount}
            pxPerMinute={pxPerMinute}
            dimmed={!item.cards.some(matchesFilter)}
            selection={selection}
            callbacks={callbacks}
          />
        ))}
        {column.orphans.map((orphan) => (
          <Tick
            key={orphan.id}
            snapshot={orphan}
            topPx={topPxAt(new Date(orphan.capturedAt))}
            isSelected={orphan.id === selection.snapshotId}
            isOrphan
            onSelect={() => callbacks.onSelectSnapshot(orphan)}
          />
        ))}
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 left-0 [&>*]:pointer-events-auto"
        style={{ width: BAR_LANE_WIDTH_PX }}
      >
        {column.observation.map((span) => (
          <ObservationBar
            key={span.key}
            span={span}
            topPx={topPxAt(span.startedAt)}
            heightPx={Math.max(heightOf(span), 1)}
            isSelected={selection.intervalIds.has(span.interval.id)}
            onSelect={() => callbacks.onSelectInterval(span.interval.id)}
          />
        ))}
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 [&>*]:pointer-events-auto"
        style={{
          left: BAR_LANE_WIDTH_PX + BAR_LANE_GAP_PX,
          width: BAR_LANE_WIDTH_PX,
        }}
      >
        {column.presence.map((span) => (
          <button
            type="button"
            key={span.interval.id}
            title={[
              `present · ${formatClockRangeWithSeconds(span.startedAt, span.endedAt)} · ${formatDuration(span.endedAt.getTime() - span.startedAt.getTime())}`,
              ...span.violations.map((violation) => violation.message),
            ].join('\n')}
            aria-pressed={selection.intervalIds.has(span.interval.id)}
            onClick={() => callbacks.onSelectInterval(span.interval.id)}
            className={cn(
              'absolute inset-x-0 cursor-default rounded-sm bg-foreground-muted opacity-60',
              span.violations.length > 0 && 'bg-warning-9',
              selection.intervalIds.has(span.interval.id) &&
                'opacity-100 ring-2 ring-accent'
            )}
            style={{
              top: topPxAt(span.startedAt),
              height: Math.max(heightOf(span), 1),
            }}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Callers must not render this for a day with no columns, because the drawn hours come from the
 * day's intervals and captures.
 */
export const ActivityRail = ({
  shape,
  dayEnd,
  pxPerMinute,
  appFilter,
  selection,
  ...callbacks
}: {
  shape: ActivityDayShape;
  dayEnd: Date;
  pxPerMinute: number;
  appFilter: string | null;
  selection: RailSelection;
} & RailCallbacks) => {
  const showsNow = shape.now < dayEnd;
  const drawn = shape.columns.flatMap((column) => [
    ...column.observation,
    ...column.presentSpans,
    ...column.cards,
    ...column.locks,
    ...column.orphans.map((orphan) => {
      const capturedAt = new Date(orphan.capturedAt);
      return { startedAt: capturedAt, endedAt: capturedAt };
    }),
    ...(showsNow ? [{ startedAt: shape.now, endedAt: shape.now }] : []),
  ]);
  const railStart = startOfHour(
    new Date(Math.min(...drawn.map((span) => span.startedAt.getTime())))
  );
  const railEnd = new Date(
    Math.min(
      addHours(
        startOfHour(
          new Date(Math.max(...drawn.map((span) => span.endedAt.getTime())))
        ),
        1
      ).getTime(),
      dayEnd.getTime()
    )
  );

  const topPxAt = (moment: Date) =>
    ((moment.getTime() - railStart.getTime()) / 60_000) * pxPerMinute;

  const intervalMinutes = gridlineIntervalMinutes(pxPerMinute);
  // Rounded rather than floored so a DST day still ends on a labelled gridline
  const gridlineCount = Math.round(
    (railEnd.getTime() - railStart.getTime()) / 60_000 / intervalMinutes
  );
  const nowPx = topPxAt(shape.now);

  return (
    <div className="relative" style={{ height: topPxAt(railEnd) }}>
      {Array.from({ length: gridlineCount + 1 }, (_, index) => {
        const moment = addMinutes(railStart, index * intervalMinutes);
        // Hides the gridline label where the now label would overlap it
        const hidden = showsNow && Math.abs(topPxAt(moment) - nowPx) < 12;
        return (
          <Gridline
            key={index}
            label={hidden ? '' : format(moment, 'HH:mm')}
            topPx={topPxAt(moment)}
            isHour={moment.getMinutes() === 0}
          />
        );
      })}
      <XStack
        className={cn(
          'absolute inset-y-0 right-0',
          TRACK_INSET_CLASS,
          COLUMN_GAP_CLASS
        )}
      >
        {shape.columns.map((column) => (
          <Column
            key={column.deviceId}
            column={column}
            topPxAt={topPxAt}
            railHeightPx={topPxAt(railEnd)}
            pxPerMinute={pxPerMinute}
            appFilter={appFilter}
            selection={selection}
            callbacks={callbacks}
          />
        ))}
      </XStack>
      {showsNow ? (
        <div
          data-now-line
          className="pointer-events-none absolute inset-x-0 z-10 flex items-center"
          style={{ top: nowPx }}
        >
          <span
            className={cn(
              GUTTER_CLASS,
              'shrink-0 pr-3 text-right text-[10px] font-medium text-danger-11'
            )}
          >
            now
          </span>
          <div className="h-px flex-1 bg-danger-9" />
        </div>
      ) : null}
    </div>
  );
};
